import * as React from "react";
import { Input } from "./input";
import { cn } from "src/vibe-code/rcm-v2/lib/utils";

export interface PhoneInputProps
  extends Omit<React.ComponentProps<"input">, "type" | "onChange" | "value"> {
  /**
   * The phone number value in "+1XXXXXXXXXX" format (e.g., "+12105551234")
   * or partial format like "+1210" for incomplete numbers
   * or 10 digits for backward compatibility (e.g., "2105551234")
   */
  value?: string;
  /**
   * Callback when the phone number changes (returns "+1" + digits format, e.g., "+1210" or "+12105551234")
   * Updates on every keypress, even for incomplete numbers
   */
  onChange?: (value: string) => void;
}

/**
 * Formats a string of digits into the phone number format: ###-###-####
 */
const formatPhoneNumber = (digits: string): string => {
  const cleaned = digits.replace(/\D/g, "").slice(0, 10);

  if (cleaned.length === 0) return "";
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
};

/**
 * Converts digits to "+1" + digits format (e.g., "+1210" or "+12105551234")
 * Returns empty string if no digits
 */
const formatWithCountryCode = (digits: string): string => {
  const cleaned = digits.replace(/\D/g, "").slice(0, 10);
  if (cleaned.length === 0) {
    return "";
  }
  return `+1${cleaned}`;
};

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, value, onChange, ...props }, ref) => {
    // Extract digits from the value prop (handles "+1XXXXXXXXXX" format or partial "+1XXX")
    const extractDigitsFromValue = (val: string | undefined): string => {
      if (!val) return "";
      // Remove all non-digits
      const digits = val.replace(/\D/g, "");
      // If value starts with "+1", the digits will include the 1, so remove it
      if (val.startsWith("+1")) {
        return digits.slice(1); // Remove the leading 1 from digits
      }
      // If it starts with 1 and has 11+ digits, remove the leading 1
      if (digits.length >= 11 && digits.startsWith("1")) {
        return digits.slice(1);
      }
      // Otherwise take up to 10 digits (for backward compatibility with plain digit format)
      return digits.slice(0, 10);
    };

    const digitsFromValue = extractDigitsFromValue(value);
    const [internalValue, setInternalValue] = React.useState(() =>
      formatPhoneNumber(digitsFromValue),
    );

    // Sync internal value with external value prop
    React.useEffect(() => {
      const digitsFromValue = extractDigitsFromValue(value);
      setInternalValue(formatPhoneNumber(digitsFromValue));
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      // Extract up to 10 digits from user input
      const digits = inputValue.replace(/\D/g, "").slice(0, 10);
      const formatted = formatPhoneNumber(digits);

      setInternalValue(formatted);

      // Always call onChange with "+1" + digits format (updates on every keypress)
      // Empty string if no digits, otherwise "+1" + digits (e.g., "+1210" or "+12105551234")
      const valueWithCountryCode = formatWithCountryCode(digits);

      onChange?.(valueWithCountryCode);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow: backspace, delete, tab, escape, enter, arrows
      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ];

      if (allowedKeys.includes(e.key)) return;

      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (e.ctrlKey || e.metaKey) return;

      // Block non-numeric input (except the dash which we format ourselves)
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    };

    return (
      <Input
        {...props}
        ref={ref}
        type="tel"
        inputMode="numeric"
        value={internalValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className={cn(className)}
      />
    );
  },
);

PhoneInput.displayName = "PhoneInput";

export { PhoneInput };
